<template>
  <div>
    <div class="maintenance-loading-banner" v-if="portalLoading">
      <MaintenanceBannerVue />
    </div>
    <div v-if="portalLoading" id="pre-loader-wrapper">
      <div id="pre-loader">
        <div class="loader"></div>
        <h2>Loading {{ this.loadingText }}...</h2>
      </div>
    </div>
    <header>
      <div>
        <h1>Dashboard - Hanex RMA Portal</h1>
      </div>
    </header>
    <div id="main-content">
      <div class="content-wrapper" id="button-bar">
        <button @click="switchPage()">
          {{ $mlGet("dashboard-createNewRma") }}
        </button>
      </div>
      <div class="content-wrapper">
        <h2>
          {{ $mlGet("dashboard-yourRmas-head") }}
        </h2>
        <table>
          <tr>
            <th>{{ $mlGet("rma-header-number") }}</th>
            <th>{{ $mlGet("rma-header-make") }}</th>
            <th>{{ $mlGet("rma-header-model") }}</th>
            <th>{{ $mlGet("rma-header-serialnumber") }}</th>
            <th>{{ $mlGet("rma-header-status") }}</th>
            <th>{{ $mlGet("rma-header-comment") }}</th>
            <th>{{ $mlGet("rma-header-timecreated") }}</th>
          </tr>
          <tr v-for="rma in yourRmaList" :key="rma.rmaId">
            <td>{{ rma.rmaId }}</td>
            <td>{{ rma.model.make.name }}</td>
            <td>{{ rma.model.name }}</td>
            <td>{{ rma.serialnumber }}</td>
            <td>{{ getStatus(rma) }}</td>
            <td>{{ rma.comment }}</td>
            <td>{{ cleanDate(rma.tsCreated) }}</td>
          </tr>
        </table>
      </div>
      <div>
        <TheAdministratorView v-if="isAdministrator" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";

import { getLanguageFields } from "@/multilanguage";
import { ResponseRma } from "@/types";

import TheAdministratorView from "@/components/TheAdministratorView.vue";
import { AxiosError } from "axios";

import { cleanDate } from "@/utils/utils";
import MaintenanceBannerVue from "@/components/MaintenanceBanner.vue";

@Component({
  components: {
    TheAdministratorView,
    MaintenanceBannerVue
  }
})
export default class Dashboard extends Vue {
  private loadingText = "Requests";
  private portalLoading = true;

  private yourRmaList: ResponseRma[] = [];
  private isAdministrator = false;

  public cleanDate = cleanDate;

  switchPage(): void {
    this.$router.push("/rma/create");
  }

  async mounted(): Promise<void> {
    this.loadingText = "authentication";

    // Refreshing token with Joomla token
    if (this.$store.state?.isShadow !== true)
      await this.joomlaAuthentication().catch(() => {
        console.info("Joomla token wasn't available.");
      });

    try {
      this.loadingText = "language fields";
      const languageResponse = await getLanguageFields(this);
      this.$store.state.localLanguagePack = languageResponse;
      this.loadingText = "saved RMA's";
      await this.getRmaList();
      this.loadingText = "user information";
      await this.getUserThumb();
      this.loadingText = "RMA helper data";
      await this.$store.getters.getRmaReasons;
      this.portalLoading = false;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status == 401 || error.response?.status == 403) {
          console.info(
            "Required requests were unsuccessful, redirecting to login."
          );
          if (this.$router.currentRoute.path !== "/login")
            this.$router.push("/login");
        }
      } else {
        console.error(error);
      }
    }
  }

  async getUserThumb(): Promise<void> {
    this.$axios
      .get<{ isAdministrator: boolean }>("/user/thumb")
      .then(response => {
        this.isAdministrator = response.data.isAdministrator;
      });
    return;
  }

  getStatus(rma: ResponseRma): string {
    let result = "";
    if (rma.rmaConcept) result = this.$mlGet("rma-status-concept");
    if (rma.rmaReadyForProcessing)
      result = this.$mlGet("rma-status-readyForProcessing");
    if (rma.rmaInProcess) result = this.$mlGet("rma-status-process");
    return result;
  }

  /**
   * Authenticate with Joomla token
   *
   * @returns {Promise<void>}
   * @throws {Error} Axios error
   */
  async joomlaAuthentication(): Promise<void> {
    // Try to authenticate with Joomla token
    const response = await this.$axios.post("/login/joomla");
    if (response && response.data) {
      this.$store.commit("setToken", response.data.token);
      this.$axios.defaults.headers.common.Authorization =
        "Bearer " + localStorage.getItem("authenticationToken");
      return;
    }
  }

  async getRmaList(): Promise<void> {
    await this.$axios
      .get("/rmas?complete=true")
      .then(response => (this.yourRmaList = response.data));
  }

  logout(): void {
    this.$store.commit("setToken", "");
    this.$router.push("/login");
  }
}
</script>

<style>
body {
  margin: 0;
  padding: 0;
}
</style>

<style scoped>
header {
  width: 100%;
  background-color: #001538;
  border-bottom: 2px solid lightgray;
  color: white;
}

h1 {
  margin: 0;
  padding: 1.5ex;
}

.content-wrapper {
  padding: 2ex;
}

button {
  padding: 1ex;
  background-color: #da6f38;
  border-radius: 3px;
  border: none;
  cursor: pointer;
  font-size: 120%;
  color: white;
  font-weight: bold;
}

button:hover {
  background-color: rgb(221, 136, 51);
}

button:active {
  font-weight: bold;
}

table {
  border-collapse: collapse;
  width: 100%;
}

th,
td {
  padding: 5px;
  font-size: 92%;
}

th {
  background-color: #001538;
  color: white;
  font-weight: normal;
  font-family: "Overpass", sans-serif !important;
  padding: 4px;
  padding-top: 6px;
  text-align: left;
  border-left: 1px solid gray;
  font-weight: 200;
}

td {
  border-left: 1px dotted gray;
  border-bottom: 1px dotted gray;
}

#pre-loader-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 0;
}

#pre-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}

.maintenance-loading-banner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  z-index: 1;
}
</style>
